import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { getBlogPost, getBlogPosts, BlogPost as BlogPostType } from '../services/api';
import { getCurrentDomain } from '../config/domains';
import { useAuth } from '../context/AuthContext';
import {
  Comment,
  getComments,
  createComment,
  updateComment,
  deleteComment,
} from '../services/auth';

const BlogPost: React.FC = () => {
  const domain = getCurrentDomain();
  const { slug } = useParams<{ slug: string }>();
  const { isAuthenticated, user, isAdmin } = useAuth();

  const [post, setPost] = useState<BlogPostType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextPost, setNextPost] = useState<BlogPostType | null>(null);
  const [prevPost, setPrevPost] = useState<BlogPostType | null>(null);

  // Comments state
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentContent, setCommentContent] = useState('');
  const [editingComment, setEditingComment] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');
  const [commentError, setCommentError] = useState('');
  const [commentSuccess, setCommentSuccess] = useState('');

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) return;

      setLoading(true);
      const response = await getBlogPost(slug);
      if (response.error) {
        setError(response.error);
      } else {
        setPost(response.data || null);
      }
      setLoading(false);
    };

    fetchPost();
  }, [slug]);

  // Fetch next/previous posts
  useEffect(() => {
    const fetchNavPosts = async () => {
      if (!slug) return;

      // Fetch up to 500 posts to ensure prev/next navigation works for large blogs.
      // TODO: replace with server-side prev/next endpoints when implemented.
      const response = await getBlogPosts(500, 0);
      if (response.data?.posts) {
        const posts = response.data.posts;
        const currentIndex = posts.findIndex(p => p.slug === slug);

        if (currentIndex !== -1) {
          // Posts are ordered newest-first (published_at DESC).
          // "Previous Post" (chronologically older) = higher array index.
          // "Next Post" (chronologically newer) = lower array index.
          if (currentIndex < posts.length - 1) {
            setPrevPost(posts[currentIndex + 1]); // older post
          } else {
            setPrevPost(null);
          }

          if (currentIndex > 0) {
            setNextPost(posts[currentIndex - 1]); // newer post
          } else {
            setNextPost(null);
          }
        }
      }
    };

    fetchNavPosts();
  }, [slug]);

  // Fetch comments
  useEffect(() => {
    const fetchComments = async () => {
      if (!slug) return;

      setCommentsLoading(true);
      const response = await getComments(slug);
      if (response.data) {
        setComments(response.data);
      }
      setCommentsLoading(false);
    };

    fetchComments();
  }, [slug]);

  if (!domain.hasBlog) {
    return (
      <div>
        <h1 className="page-title">Blog</h1>
        <p>Blog is not available for this site.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading blog post...</div>;
  }

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug) return;

    setCommentError('');
    setCommentSuccess('');

    const response = await createComment(slug, commentContent);

    if (response.error) {
      setCommentError(response.error);
    } else if (response.data) {
      setComments([...comments, response.data]);
      setCommentContent('');
      setCommentSuccess('Comment added successfully!');
      setTimeout(() => setCommentSuccess(''), 3000);
    }
  };

  const handleEditComment = async (commentId: number) => {
    setCommentError('');

    const response = await updateComment(commentId, editContent);

    if (response.error) {
      setCommentError(response.error);
    } else if (response.data) {
      setComments(comments.map(c => (c.id === commentId ? response.data! : c)));
      setEditingComment(null);
      setEditContent('');
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    setCommentError('');

    const response = await deleteComment(commentId);

    if (response.error) {
      setCommentError(response.error);
    } else {
      setComments(comments.filter(c => c.id !== commentId));
    }
  };

  if (error || !post) {
    return (
      <div>
        <div className="error">Blog post not found.</div>
        <Link to="/blog">Back to Blog</Link>
      </div>
    );
  }

  const renderContent = (content: string) => {
    if (/<[a-z][\s\S]*>/i.test(content)) return content;
    return content
      .split(/\n\n+/)
      .map(para => `<p>${para.replace(/\n/g, '<br>')}</p>`)
      .join('');
  };

  return (
    <div>
      <article>
        <div className="page-header">
          <h1 className="page-title" style={{ color: domain.primaryColor }}>
            {post.title}
          </h1>
          <div className="blog-post-meta">
            By {post.author} on {new Date(post.published_date).toLocaleDateString()}
          </div>
        </div>

        <div className="page-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderContent(post.content)) }} />

        {post.tags && post.tags.length > 0 && (
          <div className="blog-tags">
            {post.tags.map((tag) => (
              <Link
                key={tag}
                to={`/blog/tag/${encodeURIComponent(tag)}`}
                className="blog-tag blog-tag-link"
                style={{ textDecoration: 'none' }}
              >
                {tag}
              </Link>
            ))}
          </div>
        )}

        {/* Next/Previous Post Navigation */}
        {(prevPost || nextPost) && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '3rem',
            paddingTop: '2rem',
            borderTop: '1px solid #e9ecef',
            gap: '1rem'
          }}>
            <div style={{ flex: 1 }}>
              {prevPost && (
                <Link
                  to={`/blog/${prevPost.slug}`}
                  style={{
                    display: 'block',
                    padding: '1rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                >
                  <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.5rem' }}>
                    ← Previous Post
                  </div>
                  <div style={{ color: domain.primaryColor, fontWeight: '500' }}>
                    {prevPost.title}
                  </div>
                </Link>
              )}
            </div>
            <div style={{ flex: 1, textAlign: 'right' }}>
              {nextPost && (
                <Link
                  to={`/blog/${nextPost.slug}`}
                  style={{
                    display: 'block',
                    padding: '1rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                >
                  <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.5rem' }}>
                    Next Post →
                  </div>
                  <div style={{ color: domain.primaryColor, fontWeight: '500' }}>
                    {nextPost.title}
                  </div>
                </Link>
              )}
            </div>
          </div>
        )}
      </article>

      {/* Comments Section */}
      <div className="comments-section" style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '2px solid #eee' }}>
        <h2 style={{ marginBottom: '1.5rem' }}>
          Comments ({comments.length})
        </h2>

        {commentSuccess && (
          <div style={{
            padding: '12px',
            backgroundColor: '#efe',
            border: '1px solid #cfc',
            borderRadius: '4px',
            marginBottom: '20px',
            color: '#060'
          }}>
            {commentSuccess}
          </div>
        )}

        {commentError && (
          <div style={{
            padding: '12px',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            marginBottom: '20px',
            color: '#c00'
          }}>
            {commentError}
          </div>
        )}

        {/* Add Comment Form (only for authenticated users) */}
        {isAuthenticated ? (
          <form onSubmit={handleAddComment} style={{ marginBottom: '2rem' }}>
            <div className="form-group">
              <label htmlFor="comment">Add a Comment</label>
              <textarea
                id="comment"
                value={commentContent}
                onChange={(e) => setCommentContent(e.target.value)}
                required
                placeholder="Share your thoughts..."
                rows={4}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  resize: 'vertical'
                }}
              />
            </div>
            <button type="submit" className="btn btn-primary">
              Post Comment
            </button>
          </form>
        ) : (
          <div style={{
            padding: '20px',
            backgroundColor: '#f5f5f5',
            borderRadius: '4px',
            marginBottom: '2rem',
            textAlign: 'center'
          }}>
            <p>
              <Link to="/login" style={{ color: domain.primaryColor }}>Login</Link> to leave a comment
            </p>
          </div>
        )}

        {/* Comments List */}
        {commentsLoading ? (
          <div>Loading comments...</div>
        ) : comments.length === 0 ? (
          <p style={{ color: '#999', fontStyle: 'italic' }}>
            No comments yet. Be the first to comment!
          </p>
        ) : (
          <div className="comments-list">
            {comments.map((comment) => (
              <div
                key={comment.id}
                style={{
                  padding: '1.5rem',
                  backgroundColor: '#f9f9f9',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  border: '1px solid #eee'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '0.5rem'
                }}>
                  <div>
                    <strong style={{ color: domain.primaryColor }}>
                      {comment.user_email}
                    </strong>
                    <span style={{ color: '#999', fontSize: '0.9rem', marginLeft: '10px' }}>
                      {new Date(comment.created_at).toLocaleDateString()} at{' '}
                      {new Date(comment.created_at).toLocaleTimeString()}
                    </span>
                    {comment.created_at !== comment.updated_at && (
                      <span style={{ color: '#999', fontSize: '0.85rem', marginLeft: '5px', fontStyle: 'italic' }}>
                        (edited)
                      </span>
                    )}
                  </div>

                  {/* Edit/Delete buttons for comment owner or admin */}
                  {isAuthenticated && (user?.id === comment.user_id || isAdmin) && (
                    <div>
                      {user?.id === comment.user_id && editingComment !== comment.id && (
                        <button
                          onClick={() => {
                            setEditingComment(comment.id);
                            setEditContent(comment.content);
                          }}
                          style={{
                            marginRight: '8px',
                            padding: '4px 12px',
                            fontSize: '14px',
                            color: domain.primaryColor,
                            background: 'none',
                            border: `1px solid ${domain.primaryColor}`,
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Edit
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        style={{
                          padding: '4px 12px',
                          fontSize: '14px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>

                {/* Comment content or edit form */}
                {editingComment === comment.id ? (
                  <div>
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '10px',
                        fontSize: '16px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        marginBottom: '10px',
                        resize: 'vertical'
                      }}
                    />
                    <button
                      onClick={() => handleEditComment(comment.id)}
                      className="btn btn-primary"
                      style={{ marginRight: '8px' }}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditingComment(null);
                        setEditContent('');
                      }}
                      className="btn btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <p style={{ margin: '0.5rem 0 0 0', lineHeight: '1.6' }}>
                    {comment.content}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPost;
